import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Link,
  OutlinedInput,
  Typography
} from '@mui/material';

import {
  fieldLabelSx,
  forgotLinkSx,
  formControlSx,
  formWrapperSx,
  requiredSx,
  signInButtonSx,
  signInSubtitleSx,
  signInTitleSx
} from '../../Common/components/Styles/AuthStyles';

const ForgotPasswordForm = ({
  formState,
  hasError,
  onBack,
  onChange,
  onSubmit
}) => {
  const usernameId = React.useId();
  const emailId = React.useId();

const getErrorMessage = field => {
  const isTouched = formState.touched?.[field];

  if (!isTouched) return '';

  return formState.errors[field]?.[0] || '';
};
  return (
    <Box component="form" noValidate onSubmit={onSubmit} sx={formWrapperSx}>
      <Box>
        <Typography component="h2" sx={signInTitleSx}>
          Forgot Password
        </Typography>

        <Typography sx={signInSubtitleSx}>
          Enter your username and email address to reset your password.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0
        }}>
        <Box>
          <Typography component="label" htmlFor={usernameId} sx={fieldLabelSx}>
            Username
            <Box component="span" sx={requiredSx}>
              *
            </Box>
          </Typography>

          <FormControl
            error={hasError('username')}
            fullWidth
            sx={formControlSx}
            variant="outlined">
            <OutlinedInput
              autoComplete="username"
              id={usernameId}
              name="username"
              onChange={onChange}
              placeholder="Enter your username"
              type="text"
              value={formState.values.username}
            />
            <FormHelperText
              sx={{
                minHeight: 16,
                visibility: getErrorMessage('username') ? 'visible' : 'hidden'
              }}>
              {getErrorMessage('username')}
            </FormHelperText>
          </FormControl>
        </Box>

        <Box>
          <Typography component="label" htmlFor={emailId} sx={fieldLabelSx}>
            Email
            <Box component="span" sx={requiredSx}>
              *
            </Box>
          </Typography>

          <FormControl
            error={Boolean(formState.touched?.email && hasError('email'))}
            fullWidth
            sx={formControlSx}
            variant="outlined">
            <OutlinedInput
              autoComplete="email"
              id={emailId}
              name="email"
              onChange={onChange}
              placeholder="Enter the email address associated with your account"
              type="email"
              value={formState.values.email}
            />
            <FormHelperText
              sx={{
                minHeight: 16,
                visibility: getErrorMessage('email') ? 'visible' : 'hidden'
              }}>
              {getErrorMessage('email')}
            </FormHelperText>
          </FormControl>
        </Box>
      </Box>

      <Button
        disabled={!formState.isValid}
        fullWidth
        sx={signInButtonSx}
        type="submit"
        variant="contained">
        Reset Password
      </Button>

      <Typography
        sx={{
          color: '#757575',
          fontSize: 12,
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
        Remember your password?{' '}
        <Link
          component="button"
          onClick={onBack}
          sx={{
            ...forgotLinkSx,
            background: 'none',
            border: 0,
            cursor: 'pointer',
            fontStyle: 'normal',
            fontWeight: 600,
            px: 0.5
          }}
          type="button">
          Sign In
        </Link>
      </Typography>
    </Box>
  );
};

ForgotPasswordForm.propTypes = {
  formState: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    isValid: PropTypes.bool.isRequired,
    values: PropTypes.shape({
      email: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  hasError: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default ForgotPasswordForm;
